import { create } from 'zustand';

/**
 * The merchant's chat as it is laid out right now: whether the floating panel
 * is open, which of its two tabs is showing, and which group is open in the
 * Groups tab.
 *
 * Survives navigation on purpose. The router keys the whole tree by pathname,
 * so every page change remounts the bubble - plain `useState` would slam the
 * panel shut each time the user follows a link mid-conversation. Same reason
 * the sidebar's open state lives in a store.
 *
 * The chat page reads the same tab and group, so moving between the panel and
 * the page keeps you in the conversation you were in, and a notification can
 * open a group without knowing which of the two is on screen. Out here rather
 * than inside a component because that notification is raised from the
 * socket, which has no component to reach through.
 */
export type MerchantChatTab = 'support' | 'groups';

type ChatBubbleState = {
  open: boolean;
  setOpen: (open: boolean) => void;

  tab: MerchantChatTab;
  setTab: (tab: MerchantChatTab) => void;

  /** The group open in the Groups tab, or null for its list. */
  groupId: number | null;
  /** Opens a group - or, with null, goes back to the list - on the Groups tab. */
  selectGroup: (groupId: number | null) => void;
};

export const useChatBubbleState = create<ChatBubbleState>()((set) => ({
  open: false,
  setOpen: (open) => set({ open }),

  tab: 'support',
  setTab: (tab) => set({ tab }),

  groupId: null,
  selectGroup: (groupId) =>
    set(groupId === null ? { groupId } : { groupId, tab: 'groups' }),
}));
